import Nav from './Nav';
import React, { useState, useEffect } from 'react';

const TOOL_META = {
  sermon:       { icon: '📖', name: 'Sermon' },
  announcement: { icon: '📢', name: 'Announcement' },
  discussion:   { icon: '👥', name: 'Discussion' },
  devotional:   { icon: '✍️', name: 'Devotional' },
  pastoral:     { icon: '🤝', name: 'Pastoral Care' },
  evangelism:   { icon: '💬', name: 'Evangelism' },
  prayer:       { icon: '🙏', name: 'Prayer' },
  series:       { icon: '🗓️', name: 'Series' },
  biblestudy:   { icon: '📚', name: 'Bible Study' },
  eventpromo:   { icon: '🎉', name: 'Event Promo' },
};

const DENOMINATIONS = [
  '', 'Baptist', 'Methodist', 'Pentecostal', 'Presbyterian',
  'Non-Denominational', 'Lutheran', 'Episcopal', 'Catholic', 'Other',
];

function formatMemberSince(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function ProfilePage({ token, onBack, onLogout, onAuthExpired, onNavigate, userRole, currentPage }) {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ display_name: '', church_name: '', church_denomination: '', state: '' });
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveError, setSaveError] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');

  useEffect(() => {
    fetch('/api/user/profile', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.status === 401) { onAuthExpired(); return null; } return r.json(); })
      .then(data => {
        if (!data) return;
        setProfile(data);
        setForm({
          display_name: data.display_name || '',
          church_name: data.church_name || '',
          church_denomination: data.church_denomination || '',
          state: data.state || '',
        });
        setLoading(false);
        // Fetch subscription status
        fetch('/api/user/subscription', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data) setSubscription(data); })
          .catch(() => {});
      })
      .catch(() => setLoading(false));
  }, [token, onAuthExpired]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveMsg(''); setSaveError('');
    try {
      const res = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setSaveError(data.error || 'Failed to save.'); }
      else { setSaveMsg('Profile saved successfully.'); setProfile(p => ({ ...p, ...form })); }
    } catch { setSaveError('Error saving profile.'); }
    finally { setSaving(false); }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdLoading(true); setPwdMsg(''); setPwdError('');
    if (pwdForm.new_password !== pwdForm.confirm_password) {
      setPwdError('New passwords do not match.'); setPwdLoading(false); return;
    }
    if (pwdForm.new_password.length < 8) {
      setPwdError('New password must be at least 8 characters.'); setPwdLoading(false); return;
    }
    try {
      const res = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(pwdForm),
      });
      const data = await res.json();
      if (!res.ok) { setPwdError(data.error || 'Failed to change password.'); }
      else { setPwdMsg('Password updated successfully.'); setPwdForm({ current_password: '', new_password: '', confirm_password: '' }); }
    } catch { setPwdError('Error changing password.'); }
    finally { setPwdLoading(false); }
  };


  const handlePortal = async () => {
    setPortalLoading(true);
    setPortalError('');
    try {
      const res = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) { setPortalError(data.error || 'Failed to open billing portal.'); setPortalLoading(false); return; }
      window.location.href = data.url;
    } catch {
      setPortalError('Something went wrong. Please try again.');
      setPortalLoading(false);
    }
  };

  const isPro = profile?.role === 'admin' || profile?.role === 'pro';
  const monthlyLimit = isPro ? null : 5;
  const monthlyUsed = profile?.generations_used_this_month ?? 0;
  const pct = monthlyLimit ? Math.min(100, (monthlyUsed / monthlyLimit) * 100) : 0;


  function formatProExpiry(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Nav */}
      <Nav onNavigate={onNavigate} onLogout={onLogout} userRole={userRole} currentPage={currentPage} />

      <main className="flex-1 px-4 py-10 max-w-2xl mx-auto w-full">
        <h2 className="text-2xl font-bold mb-8" style={{ color: '#1B3A5C' }}>Your Profile</h2>

        {loading ? (
          <p className="text-gray-400 text-center py-20">Loading...</p>
        ) : (
          <div className="space-y-6">

            {/* ── SECTION 1: Account Info ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>Account Information</h3>
              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full border border-gray-200 rounded-lg px-4 py-2 text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    placeholder="Pastor John Smith"
                    value={form.display_name}
                    onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Church Name</label>
                  <input
                    type="text"
                    placeholder="Grace Community Church"
                    value={form.church_name}
                    onChange={e => setForm(f => ({ ...f, church_name: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Denomination</label>
                  <select
                    value={form.church_denomination}
                    onChange={e => setForm(f => ({ ...f, church_denomination: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900 bg-white"
                  >
                    {DENOMINATIONS.map(d => (
                      <option key={d} value={d}>{d || '— Select denomination —'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    placeholder="e.g. Texas"
                    value={form.state}
                    onChange={e => setForm(f => ({ ...f, state: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                {saveMsg && <p className="text-sm text-green-600">{saveMsg}</p>}
                {saveError && <p className="text-sm text-red-600">{saveError}</p>}
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full py-2.5 rounded-lg text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#C8921A' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>

            {/* ── SECTION 2: Change Password ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>Change Password</h3>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={pwdForm.current_password}
                    onChange={e => setPwdForm(f => ({ ...f, current_password: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={pwdForm.new_password}
                    onChange={e => setPwdForm(f => ({ ...f, new_password: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwdForm.confirm_password}
                    onChange={e => setPwdForm(f => ({ ...f, confirm_password: e.target.value }))}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
                  />
                </div>
                {pwdMsg && <p className="text-sm text-green-600">{pwdMsg}</p>}
                {pwdError && <p className="text-sm text-red-600">{pwdError}</p>}
                <button
                  type="submit"
                  disabled={pwdLoading}
                  className="w-full py-2.5 rounded-lg text-white font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#C8921A' }}
                >
                  {pwdLoading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>

            {/* ── SECTION 3: Usage Stats ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>Usage & Plan</h3>

              {/* Plan badge */}
              <div className="flex items-center gap-3 mb-5">
                <span
                  className="px-3 py-1 rounded-full text-sm font-semibold"
                  style={isPro
                    ? { backgroundColor: '#FEF3C7', color: '#92400E' }
                    : { backgroundColor: '#F3F4F6', color: '#6B7280' }}
                >
                  {profile?.role === 'admin' ? '🛡️ Admin' : isPro ? '⭐ Pastor Pro' : '🆓 Free'}
                </span>
                <span className="text-sm text-gray-500">Member since {formatMemberSince(profile?.member_since)}</span>
              </div>

              {/* Monthly usage */}
              <div className="mb-5">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 font-medium">This month's generations</span>
                  <span className="text-gray-500">
                    {isPro ? `${monthlyUsed} used (Unlimited)` : `${monthlyUsed} of ${monthlyLimit} used`}
                  </span>
                </div>
                {!isPro && (
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all"
                      style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? '#EF4444' : '#C8921A' }}
                    />
                  </div>
                )}
              </div>

              {/* Total */}
              <p className="text-sm text-gray-500 mb-5">
                <span className="font-semibold text-gray-700">{profile?.total_generations ?? 0}</span> total generations all time
              </p>

              {/* Tool breakdown */}
              <h4 className="text-sm font-medium text-gray-600 mb-3">Generations by tool</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(TOOL_META).map(([key, { icon, name }]) => {
                  const count = profile?.generations_by_tool?.[key] ?? 0;
                  return (
                    <div key={key} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-base">{icon}</span>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{name}</p>
                        <p className="text-xs text-gray-400">{count} gen{count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>


            {/* ── SECTION 3.5: Subscription / Billing ── */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-base font-semibold mb-5" style={{ color: '#1B3A5C' }}>Subscription</h3>

              {isPro && profile?.role !== 'admin' ? (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 rounded-full text-sm font-semibold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                      ⭐ Pastor Pro
                    </span>
                    {subscription?.pro_expires_at && (
                      <span className="text-sm text-gray-500">
                        Active through {formatProExpiry(subscription.pro_expires_at)}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    Manage your subscription, update payment method, or cancel anytime from the billing portal.
                  </p>
                  {portalError && <p className="text-sm text-red-500 mb-3">{portalError}</p>}
                  <button
                    onClick={handlePortal}
                    disabled={portalLoading}
                    className="px-6 py-2.5 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 disabled:opacity-60 text-white"
                    style={{ backgroundColor: '#1B3A5C' }}
                  >
                    {portalLoading ? 'Opening portal...' : 'Manage Subscription'}
                  </button>
                </div>
              ) : profile?.role === 'admin' ? (
                <p className="text-sm text-gray-500">Admin accounts have full access.</p>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    You are on the <span className="font-semibold">Free plan</span> (5 generations/month).
                    Upgrade to Pastor Pro for unlimited access to all 10 tools.
                  </p>
                  <button
                    onClick={() => onNavigate('upgrade')}
                    className="px-6 py-2.5 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90 text-white"
                    style={{ backgroundColor: '#C8921A' }}
                  >
                    Upgrade to Pastor Pro →
                  </button>
                </div>
              )}
            </div>

            {/* ── SECTION 4: Danger Zone ── */}
            <div className="bg-white rounded-2xl border-2 border-red-200 p-6 shadow-sm">
              <h3 className="text-base font-semibold text-red-600 mb-2">Danger Zone</h3>
              <p className="text-sm text-gray-500 mb-4">Signing out will clear your session from this device.</p>
              <button
                onClick={onLogout}
                className="px-6 py-2.5 rounded-lg border-2 border-red-400 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors"
              >
                Sign Out
              </button>
            </div>

          </div>
        )}
      </main>
    </div>
  );
}
